/* ==========================================================================
   Quiz landing page — quiz engine

   A fixed, linear sequence of questions. One tap per answer, no branching:
   the only shortcut is the kids range, which has a single size and so skips
   straight to the results.

   Everything the merchant can edit (copy, images, option labels) arrives from
   the section as JSON. Everything that the matching depends on — the question
   keys and the option values — is fixed here, because the ranking file keys
   off those exact strings.

   On completion the engine loads the ranking file (assets/quiz.json), scores
   every style against the four measured answers, and hands the ranked list to
   the results screen through a `quiz-lp:complete` event. The results screen
   can also read the same payload back out of QuizLP.getResult(), so it does
   not matter which of the two scripts finishes booting first.
   ========================================================================== */
(function () {
  'use strict';

  var LETTERS = ['A', 'B', 'C', 'D', 'E'];
  var STORAGE_KEY = 'quiz-lp:result';
  /* Shopify reserves `view` — it selects an alternate template — so the
     handover to the results screen is flagged with our own parameter. */
  var RESULTS_PARAM = 'step';
  /* Fixed, and in question order — these name both the state and the query
     string the results screen reads. */
  var ANSWER_KEYS = ['audience', 'range', 'width', 'height', 'thickness', 'shape'];

  /* ------------------------------------------------------------- storage

     The finished run lives in localStorage, so it outlives the tab: a shopper
     who closes the page and comes back to the results link later still gets
     their own order and fit marks instead of the unranked fallback.

     Because it now outlives the session, the stored payload carries a version.
     A deploy that changes the answer keys or the payload shape would otherwise
     meet a stored run it cannot read, and there is no longer an end of session
     to clear it — the guard in readStoredResult drops it instead.

     `redirecting` is deliberately not stored. It describes one handover that
     is already over by the time anything reads this back. */
  var STORAGE_VERSION = 1;

  function saveResult(result) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        v: STORAGE_VERSION,
        savedAt: Date.now(),
        answers: result.answers,
        range: result.range,
        labels: result.labels,
        ranked: result.ranked
      }));
    } catch (e) {
      /* Private browsing, or the quota is full. The run itself is unaffected —
         only a return visit falls back to the unranked grid. */
    }
  }

  function readStoredResult() {
    var raw = null;
    try {
      raw = localStorage.getItem(STORAGE_KEY);
    } catch (e) {
      return null;
    }
    if (!raw) return null;

    var stored = null;
    try {
      stored = JSON.parse(raw);
    } catch (e) {
      /* Truncated or hand-edited — treat it as absent. */
    }
    if (!stored || stored.v !== STORAGE_VERSION || !stored.answers) {
      clearStoredResult();
      return null;
    }
    return stored;
  }

  function clearStoredResult() {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (e) {
      /* nothing stored to clear */
    }
  }

  /* The answer values above are the quiz's own vocabulary; the ranking file
     uses its own. These three tables are the only bridge between them. */
  var GENDER_BY_RANGE = { ladies: 'ladies', mens: 'mens' };
  var WIDTH_BANDS = { small: 'small', average: 'average', large: 'large' };
  var HEIGHT_BANDS = { short: 'small', medium: 'average', tall: 'large' };
  var THICKNESS_BANDS = { slim: 'slim-standard-frames', chunky: 'chunky-thick-frames' };
  /* The quiz groups shapes so the shopper has five choices instead of eight.
     A grouped answer scores against each shape it covers, best score wins. */
  var SHAPE_BANDS = {
    roundoval: ['round', 'oval'],
    rectsquare: ['rectangle', 'square'],
    cateye: ['cat-eye'],
    oversized: ['oversized'],
    aviator: ['aviator-curved', 'browline']
  };

  var PLACEHOLDER_FIGURE =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
    '<rect x="3" y="4" width="18" height="16" rx="2"/><circle cx="8.5" cy="9.5" r="1.5"/><path d="M21 15l-5-5-9 9"/></svg>';

  /* Is this URL the results handover, for the step name the section names? */
  function isResultsUrl(step, search) {
    if (!step) return false;
    var params = new URLSearchParams(search == null ? window.location.search : search);
    return params.get(RESULTS_PARAM) === step;
  }

  function escapeHtml(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function prefersReducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  /* ---------------------------------------------------------------- ranking */

  /* A style's place in a band list IS its priority — first is the strongest
     match. Normalising by list length keeps a short list (one candidate) from
     outweighing a long one, so the four dimensions stay comparable. */
  function bandScore(list, keys) {
    if (!list) return { score: 0, rank: -1 };
    for (var k = 0; k < keys.length; k++) {
      var i = list.indexOf(keys[k]);
      if (i >= 0) return { score: (list.length - i) / list.length, rank: i };
    }
    return { score: 0, rank: -1 };
  }

  function bestBandScore(data, dimension, bands, keys) {
    var dim = data && data[dimension];
    if (!dim) return { score: 0, rank: -1 };
    var best = { score: 0, rank: -1 };
    for (var i = 0; i < bands.length; i++) {
      var got = bandScore(dim[bands[i]], keys);
      if (got.rank >= 0 && (best.rank < 0 || got.score > best.score)) best = got;
    }
    return best;
  }

  /* Every style in the range's combined listing comes back, always. The
     ranking file decides the ORDER and which ones carry the right-fit mark —
     it never removes a style, so a shopper still sees the full range. */
  function rankStyles(rankingFile, answers, catalogue) {
    var styles = (catalogue || {})[answers.range] || [];
    var gender = GENDER_BY_RANGE[answers.range];
    var data = gender && rankingFile ? rankingFile[gender] : null;

    /* Kids have one size, so there is nothing to rank — the listing's own
       order stands and no style is marked as a better fit than another. */
    if (!data) {
      return styles.map(function (style, i) {
        return {
          handle: style.slug,
          style: style,
          score: 0,
          fits: false,
          ranked: false,
          order: i,
          ranks: { width: -1, height: -1, shape: -1, thickness: -1 }
        };
      });
    }

    var widthBand = WIDTH_BANDS[answers.width];
    var heightBand = HEIGHT_BANDS[answers.height];
    var thicknessBand = THICKNESS_BANDS[answers.thickness];
    var shapeBands = SHAPE_BANDS[answers.shape] || [];

    var ranked = styles.map(function (style, i) {
      /* The ranking file may name a style by its handleized name or by the
         linked product's handle — accept either. */
      var keys = [style.slug];
      if (style.child && style.child !== style.slug) keys.push(style.child);

      var width = bestBandScore(data, 'width', [widthBand], keys);
      var height = bestBandScore(data, 'height', [heightBand], keys);
      var thickness = bestBandScore(data, 'thickness', [thicknessBand], keys);
      var shape = bestBandScore(data, 'shape', shapeBands, keys);

      return {
        handle: style.slug,
        style: style,
        /* Width and height are the measurements that decide whether a pair
           physically goes over the shopper's glasses; shape and thickness
           only order what already fits, so they carry less weight. */
        score: width.score * 1.5 + height.score * 1.5 + shape.score + thickness.score,
        /* Listed for both measured bands = it clears the shopper's frame. */
        fits: width.rank >= 0 && height.rank >= 0,
        ranked: width.rank >= 0 || height.rank >= 0 || shape.rank >= 0 || thickness.rank >= 0,
        order: i,
        ranks: { width: width.rank, height: height.rank, shape: shape.rank, thickness: thickness.rank }
      };
    });

    ranked.sort(function (a, b) {
      if (a.fits !== b.fits) return a.fits ? -1 : 1;
      if (b.score !== a.score) return b.score - a.score;
      /* Same total: the closer width match goes first. */
      var aw = a.ranks.width < 0 ? 99 : a.ranks.width;
      var bw = b.ranks.width < 0 ? 99 : b.ranks.width;
      if (aw !== bw) return aw - bw;
      /* Nothing to separate them — keep the listing's own order. */
      return a.order - b.order;
    });

    return ranked;
  }

  /* ------------------------------------------------------------------ quiz */

  function Quiz(root) {
    this.root = root;
    this.config = JSON.parse(root.querySelector('[data-quiz-lp-config]').textContent);
    this.questions = this.config.questions || [];
    this.stage = root.querySelector('[data-quiz-lp-stage]');
    this.dashes = root.querySelector('[data-quiz-lp-dashes]');
    this.count = root.querySelector('[data-quiz-lp-count]');
    this.back = root.querySelector('[data-quiz-lp-back]');
    this.ctxIcon = root.querySelector('[data-quiz-lp-ctx-icon]');
    this.ctxTitle = root.querySelector('[data-quiz-lp-ctx-title]');
    this.ctxSub = root.querySelector('[data-quiz-lp-ctx-sub]');
    this.index = 0;
    this.answers = {};
    this.rankingFile = null;

    /* A reload or a shared results link lands back here with the results
       section on the same page — it owns the screen, so stay out of its way. */
    root.hidden =
      isResultsUrl(this.config.resultsStep) && !!document.querySelector('[data-quiz-lp-results]');

    this.back.addEventListener('click', this.goBack.bind(this));
    this.stage.addEventListener('click', this.onStageClick.bind(this));

    /* Warm the ranking file while the shopper is still answering, so the
       results screen is not waiting on a network round trip. */
    this.loadRanking();
    this.render();
  }

  Quiz.prototype.loadRanking = function () {
    var self = this;
    /* Served inline from the metaobject — already here, nothing to wait for and
       no round trip to warm. */
    if (this.config.ranking) {
      this.rankingFile = this.config.ranking;
      return;
    }
    if (!this.config.rankingUrl) return;
    this.rankingRequest = fetch(this.config.rankingUrl, { credentials: 'omit' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (json) { self.rankingFile = json; return json; })
      .catch(function () { return null; });
  };

  /* "your" vs "their" — the questions read differently for a gift. */
  Quiz.prototype.possessive = function () {
    return this.answers.audience === 'gift' ? 'their' : 'your';
  };

  /* The mm a band covers depends on the range: a men's "average" width is not
     a women's. The section supplies one set of cutoffs per range, and the
     option labels are written as templates around [min] and [max]. */
  Quiz.prototype.bandRange = function (dimension, band) {
    var bands = (this.config.bands || {})[this.answers.range];
    var cuts = bands && bands[dimension];
    if (!cuts) return null;
    if (band === 'small') return { min: null, max: cuts.small };
    if (band === 'average') return { min: cuts.small + 1, max: cuts.average };
    if (band === 'large') return { min: cuts.average + 1, max: null };
    return null;
  };

  Quiz.prototype.fill = function (text, tokens) {
    var out = String(text || '').replace(/\[possessive\]/g, this.possessive());
    if (tokens) {
      Object.keys(tokens).forEach(function (key) {
        out = out.split('[' + key + ']').join(tokens[key]);
      });
    }
    return out;
  };

  Quiz.prototype.renderProgress = function () {
    var self = this;
    this.dashes.innerHTML = this.questions
      .map(function (q, i) { return '<i class="' + (i < self.index ? 'is-done' : '') + '"></i>'; })
      .join('');
    this.count.textContent = this.index + 1 + '/' + this.questions.length;
    this.back.disabled = this.index === 0;
  };

  Quiz.prototype.renderBanner = function (question) {
    var banners = this.config.banners || {};
    var banner = banners[question.banner] || banners.default || {};
    if (banner.icon) {
      this.ctxIcon.src = banner.icon;
      this.ctxIcon.hidden = false;
    } else {
      this.ctxIcon.hidden = true;
    }
    this.ctxTitle.textContent = this.fill(banner.title);
    this.ctxSub.textContent = this.fill(banner.sub);
  };

  Quiz.prototype.optionLabel = function (question, option) {
    var tokens = null;
    if (question.dimension && option.band) {
      var span = this.bandRange(question.dimension, option.band);
      if (span) tokens = { min: span.min, max: span.max };
    }
    return this.fill(option.label, tokens);
  };

  Quiz.prototype.paint = function () {
    var self = this;
    var question = this.questions[this.index];
    this.renderProgress();
    this.renderBanner(question);

    var options = (question.options || [])
      .map(function (option, i) {
        return (
          '<button type="button" class="quiz-lp__opt" data-value="' + escapeHtml(option.value) + '">' +
          '<span class="quiz-lp__opt-letter" aria-hidden="true">' + LETTERS[i] + '</span>' +
          '<b>' + escapeHtml(self.optionLabel(question, option)) + '</b>' +
          '</button>'
        );
      })
      .join('');

    /* The frame is given the image's own aspect ratio before the file is
       requested, so it opens at its final height instead of standing at its
       min-height and jumping once the image decodes. Eager, not lazy: this is
       the question the shopper is looking at, so there is nothing to defer. */
    var ratio = parseFloat(question.imageRatio);
    var figure = question.image
      ? '<div class="quiz-lp__figure quiz-lp__figure--photo"' +
        (ratio > 0 ? ' style="aspect-ratio:' + ratio + '"' : '') + '>' +
        '<img src="' + escapeHtml(question.image) + '" alt="" loading="eager" decoding="async">' +
        '</div>'
      : '<div class="quiz-lp__figure">' + PLACEHOLDER_FIGURE + '<b>Image guide</b></div>';

    var sub = question.sub
      ? '<p class="quiz-lp__card-sub">' + escapeHtml(this.fill(question.sub)) + '</p>'
      : '';

    this.stage.innerHTML =
      '<div class="quiz-lp__card" data-key="' + escapeHtml(question.key) + '">' +
      '<h2>' + escapeHtml(this.fill(question.title)) + '</h2>' +
      sub +
      figure +
      '<div class="quiz-lp__opts" role="group">' + options + '</div>' +
      '</div>';

    /* Force layout so removing the class animates from the faded state
       instead of being collapsed into a single style recalculation. */
    this.stage.getBoundingClientRect();
    this.stage.classList.remove('is-fading');
  };

  Quiz.prototype.render = function () {
    if (this.stage.children.length && !prefersReducedMotion()) {
      this.stage.classList.add('is-fading');
      setTimeout(this.paint.bind(this), 160);
    } else {
      this.paint();
    }
  };

  Quiz.prototype.onStageClick = function (event) {
    var button = event.target.closest('.quiz-lp__opt');
    if (!button || !this.stage.contains(button)) return;
    var card = button.closest('[data-key]');
    this.answer(card.dataset.key, button.dataset.value);
  };

  Quiz.prototype.answer = function (key, value) {
    this.answers[key] = value;

    /* Kids come in one size, so the remaining measurements have nothing to
       decide — go straight to the results. */
    if (key === 'range' && value === 'kids') return this.complete();
    if (this.index < this.questions.length - 1) {
      this.index += 1;
      return this.render();
    }
    return this.complete();
  };

  Quiz.prototype.goBack = function () {
    if (this.index === 0) return;
    this.index -= 1;
    this.render();
  };

  Quiz.prototype.restart = function () {
    this.answers = {};
    this.index = 0;
    this.render();
    this.root.hidden = false;
    window.scrollTo(0, 0);
  };

  /* The handover URL carries the step and nothing else — the answers travel in
     localStorage, so the address bar stays clean and shareable. A results link
     opened on a browser that never took the quiz (a link passed to a friend,
     cleared site data) still renders: the results screen falls back to the
     unranked full range.

     Answers are still READ from the query string when they are there, so a
     hand-typed URL remains a way to test a particular combination. */
  Quiz.prototype.resultsUrl = function () {
    var params = new URLSearchParams(window.location.search);
    /* Anything the previous run tagged on is stale now. */
    ANSWER_KEYS.forEach(function (key) { params.delete(key); });
    if (this.config.resultsStep) params.set(RESULTS_PARAM, this.config.resultsStep);
    var query = params.toString();
    return window.location.pathname + (query ? '?' + query : '');
  };

  Quiz.prototype.complete = function () {
    var self = this;
    var finish = function () {
      var result = {
        answers: self.answers,
        range: self.answers.range,
        labels: self.answerLabels(),
        ranked: rankStyles(self.rankingFile, self.answers, self.config.styles),
        /* Whether a navigation to the results URL is about to happen. The
           results screen listens for this event so it can take over in place
           when there is no redirect — it has to know NOT to when there is one,
           or it reveals itself over the quiz for the moment before the browser
           swaps the document, and the new page then lands on top of it. */
        redirecting: !!self.config.resultsStep
      };
      QuizLP.result = result;
      saveResult(result);
      document.dispatchEvent(new CustomEvent('quiz-lp:complete', { detail: result }));

      if (self.config.resultsStep) {
        window.location.href = self.resultsUrl();
        return;
      }
      /* No results step configured — hand over in place instead. */
      self.root.hidden = true;
      window.scrollTo(0, 0);
    };

    /* The ranking file is normally warm by question six; if it is not, wait
       rather than hand the results screen an empty list. */
    if (this.rankingFile || !this.rankingRequest) finish();
    else this.rankingRequest.then(finish);
  };

  /* The results screen shows the shopper's answers back to them. */
  Quiz.prototype.answerLabels = function () {
    var self = this;
    var out = [];
    this.questions.forEach(function (question) {
      var value = self.answers[question.key];
      if (value == null) return;
      var option = (question.options || []).filter(function (o) { return o.value === value; })[0];
      out.push({
        key: question.key,
        label: question.summaryLabel || '',
        value: option ? self.optionLabel(question, option) : value
      });
    });
    return out;
  };

  /* ----------------------------------------------------------------- boot */

  var QuizLP = (window.QuizLP = window.QuizLP || {});
  QuizLP.instances = QuizLP.instances || [];
  QuizLP.result = QuizLP.result || null;
  QuizLP.rankStyles = rankStyles;
  QuizLP.answerKeys = ANSWER_KEYS;
  QuizLP.resultsParam = RESULTS_PARAM;
  QuizLP.isResultsUrl = isResultsUrl;
  /* The results screen prefers the query string — a hand-typed URL is how a
     particular combination gets tested, and it must beat the stored run. */
  QuizLP.answersFromUrl = function (search) {
    var params = new URLSearchParams(search == null ? window.location.search : search);
    var answers = {};
    var found = false;
    ANSWER_KEYS.forEach(function (key) {
      var value = params.get(key);
      if (value) {
        answers[key] = value;
        found = true;
      }
    });
    return found ? answers : null;
  };
  QuizLP.getResult = function () {
    if (QuizLP.result) return QuizLP.result;
    QuizLP.result = readStoredResult();
    return QuizLP.result;
  };
  /* Exported so the results screen clears the same key the same way, rather
     than reaching for the storage itself. */
  QuizLP.clearResult = clearStoredResult;
  QuizLP.retake = function () {
    clearStoredResult();
    QuizLP.result = null;
    document.dispatchEvent(new CustomEvent('quiz-lp:retake'));
    QuizLP.instances.forEach(function (quiz) { quiz.restart(); });
  };

  function init(scope) {
    (scope || document).querySelectorAll('[data-quiz-lp]').forEach(function (root) {
      if (root.dataset.quizLpReady) return;
      root.dataset.quizLpReady = 'true';
      QuizLP.instances.push(new Quiz(root));
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { init(); });
  else init();

  /* Theme editor: re-run when the section is re-rendered. */
  document.addEventListener('shopify:section:load', function (event) {
    QuizLP.instances = QuizLP.instances.filter(function (quiz) { return document.contains(quiz.root); });
    init(event.target);
  });
})();
